// Centralised error → user-facing message mapping.
//
// We have ~30 catch-blocks scattered across the app, each rolling its own
// "err instanceof Error ? err.message : 'Something went wrong'" pattern.
// Two problems with that:
//   1. Supabase / Edge Function / network errors leak technical jargon to
//      users ("PGRST116", "Failed to fetch", "JWT expired").
//   2. There's no single place to add a new mapping when we discover a new
//      error class in production logs.
//
// Use `mapError(err)` in catch blocks. `getErrorToast(err)` is a convenience
// for the common toast.error pattern. Phase A's hand-tuned messages still
// win when they're explicit (we only override the *generic* fallbacks).

import type { AuthError, PostgrestError } from "@supabase/supabase-js";

export type AppError = {
  message: string;
  /** True if the error reflects a transient/retryable condition (network, 5xx, rate limit). */
  retryable: boolean;
  /** Original cause — handy for Sentry / debug logs. Never shown to the user. */
  cause?: unknown;
};

const NETWORK_PATTERNS = [
  /failed to fetch/i,
  /network ?error/i,
  /load failed/i,
  /networkerror when attempting/i,
  /typeerror.*fetch/i,
  /the internet connection appears to be offline/i,
];

const RLS_PATTERNS = [
  /new row violates row-level security/i,
  /row-level security/i,
  /permission denied for/i,
];

const RATE_LIMIT_PATTERNS = [
  /rate limit/i,
  /too many requests/i,
  /too many attempts/i,
  /over_email_send_rate_limit/i,
];

const matches = (msg: string, patterns: RegExp[]): boolean =>
  patterns.some((p) => p.test(msg));

const isAuthError = (err: unknown): err is AuthError =>
  !!err && typeof err === "object" && "name" in err && err.name === "AuthApiError";

const isPostgrestError = (err: unknown): err is PostgrestError =>
  !!err &&
  typeof err === "object" &&
  "code" in err &&
  "message" in err &&
  typeof (err as { code: unknown }).code === "string";

interface FunctionsHttpError {
  context?: { status?: number };
  message?: string;
}
const isFunctionsError = (err: unknown): err is FunctionsHttpError =>
  !!err &&
  typeof err === "object" &&
  "context" in err &&
  typeof (err as FunctionsHttpError).context === "object";

/**
 * Translate any thrown value into a user-friendly AppError. Falls back to
 * a generic "something went wrong" — never leaks raw error text the user
 * can't act on.
 */
export function mapError(err: unknown): AppError {
  if (!err) return { message: "Something went wrong. Please try again.", retryable: true, cause: err };

  // Supabase Auth — the message is generally readable but we re-skin the
  // common ones for clarity.
  if (isAuthError(err)) {
    const m = err.message || "";
    if (/invalid login credentials/i.test(m)) {
      return { message: "Email or password incorrect.", retryable: false, cause: err };
    }
    if (/email not confirmed/i.test(m) || /confirm your email/i.test(m)) {
      return {
        message: "Please verify your email first. Check your inbox.",
        retryable: false,
        cause: err,
      };
    }
    if (/user already registered/i.test(m) || /already been registered/i.test(m)) {
      return {
        message: "This email is already registered. Try signing in instead.",
        retryable: false,
        cause: err,
      };
    }
    if (matches(m, RATE_LIMIT_PATTERNS)) {
      return {
        message: "Too many attempts. Please wait a few minutes.",
        retryable: true,
        cause: err,
      };
    }
    if (/password.*should be at least/i.test(m)) {
      return { message: m, retryable: false, cause: err };
    }
    return { message: m || "Authentication failed.", retryable: false, cause: err };
  }

  // PostgREST / RLS errors.
  if (isPostgrestError(err)) {
    const m = err.message || "";
    if (matches(m, RLS_PATTERNS) || err.code === "42501") {
      return {
        message: "You don't have permission to do that.",
        retryable: false,
        cause: err,
      };
    }
    if (err.code === "23505") {
      return {
        message: "That already exists. Try a different value.",
        retryable: false,
        cause: err,
      };
    }
    if (err.code === "PGRST301" || /jwt/i.test(m)) {
      return {
        message: "Your session has expired. Please sign in again.",
        retryable: false,
        cause: err,
      };
    }
    return {
      message: "Couldn't reach the server. Please try again.",
      retryable: true,
      cause: err,
    };
  }

  // supabase.functions.invoke wraps non-2xx into FunctionsHttpError.
  if (isFunctionsError(err)) {
    const status = err.context?.status ?? 0;
    if (status === 429) {
      return {
        message: "Too many requests. Please wait a moment and try again.",
        retryable: true,
        cause: err,
      };
    }
    if (status === 401 || status === 403) {
      return {
        message: "You don't have permission to do that.",
        retryable: false,
        cause: err,
      };
    }
    if (status >= 500) {
      return {
        message: "Something went wrong on our end. Try again in a moment.",
        retryable: true,
        cause: err,
      };
    }
  }

  // Plain Error / string.
  const message =
    err instanceof Error ? err.message : typeof err === "string" ? err : "";

  if (matches(message, NETWORK_PATTERNS)) {
    return {
      message: "Check your internet connection and try again.",
      retryable: true,
      cause: err,
    };
  }

  if (matches(message, RATE_LIMIT_PATTERNS)) {
    return {
      message: "Too many attempts. Please wait a few minutes.",
      retryable: true,
      cause: err,
    };
  }

  if (matches(message, RLS_PATTERNS)) {
    return {
      message: "You don't have permission to do that.",
      retryable: false,
      cause: err,
    };
  }

  // Phase A's hand-written messages reach this branch (e.g. "Too many uploads
  // in a short window. Please try again in 30s.") — we keep them as-is.
  if (message) {
    return { message, retryable: false, cause: err };
  }

  return {
    message: "Something went wrong. Please try again.",
    retryable: true,
    cause: err,
  };
}

/** Convenience for the toast.error pattern. */
export function getErrorMessage(err: unknown): string {
  return mapError(err).message;
}
